/**
 * Canonical list of exam levels used for questions, worksheets, and dropdowns.
 * Single source of truth for server models, validation, and client UI.
 * Update when adding/removing or renaming exam levels.
 */

export const EXAM_LEVELS = Object.freeze([
  'PSLE',
  'G1',
  'G2',
  'G3',
  'IP',
  'IB',
  'A-Level',
] as const);

export type ExamLevel = (typeof EXAM_LEVELS)[number];

/** Default exam level for new questions/worksheets when none is specified (e.g. bulk import). */
export const DEFAULT_EXAM_LEVEL: ExamLevel = 'G1';

/** Exam levels grouped by school tier. */
export const PRIMARY_EXAM_LEVELS = Object.freeze(['PSLE'] as const);
export const SECONDARY_EXAM_LEVELS = Object.freeze([
  'G1',
  'G2',
  'G3',
  'IP',
  'IB',
] as const);
export const JC_EXAM_LEVELS = Object.freeze(['A-Level'] as const);

export type SecondaryExamLevel = (typeof SECONDARY_EXAM_LEVELS)[number];
export type ExamLevelTier = 'primary' | 'secondary' | 'jc';

/** Returns the school tier for a given exam level. */
export function getExamLevelTier(examLevel: string): ExamLevelTier | null {
  if ((PRIMARY_EXAM_LEVELS as readonly string[]).includes(examLevel))
    return 'primary';
  if ((SECONDARY_EXAM_LEVELS as readonly string[]).includes(examLevel))
    return 'secondary';
  if ((JC_EXAM_LEVELS as readonly string[]).includes(examLevel)) return 'jc';
  return null;
}

/**
 * Valid exam levels for a given organizational level. Primary org levels map
 * to PRIMARY_EXAM_LEVELS, secondary to SECONDARY_EXAM_LEVELS, jc to
 * JC_EXAM_LEVELS. Falls back to all EXAM_LEVELS for unknown orgLevels.
 */
export function validExamLevelsForOrgLevel(
  orgLevel: string
): readonly ExamLevel[] {
  if (orgLevel.startsWith('primary_')) return PRIMARY_EXAM_LEVELS;
  if (orgLevel.startsWith('secondary_')) return SECONDARY_EXAM_LEVELS;
  if (orgLevel.startsWith('jc_')) return JC_EXAM_LEVELS;
  return EXAM_LEVELS;
}
